import _ from 'lodash';
import { SAMPLE_FROM_SCALARS } from '@/services/calculationRunner.constants';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { AGGREGATION_MODES, BIN_ERRORS, CAPSULE_MODES, Y_VALUE_BIN_MODES } from '@/tools/histogram/histogram.constants';
import { getToolConfigParams, sanitizeToolItem } from '@/tools/tool.utilities';
import { toNumber } from '@/utilities/numberHelper.utilities';
import type { StoredStatistic } from '@/tools/StatisticSelector.molecule';
import type { AggregationBinState } from '@/tools/histogram/aggregationBin.store';
import type { ToolConfigParams, ToolConfigValue, ToolItem, ToolParameter } from '@/tools/tool.types';

type AggregationMode = (typeof AGGREGATION_MODES)[keyof typeof AGGREGATION_MODES];
type CapsuleModeKey = (typeof CAPSULE_MODES)[number]['key'];
type YValueBinMode = (typeof Y_VALUE_BIN_MODES)[keyof typeof Y_VALUE_BIN_MODES];

export interface AggregationTimeBucket {
  key: string;
  display?: string;
  funct?: string;
  [key: string]: string | undefined;
}

export type Item = ToolItem & { id: string; name?: string };

export interface AggregationConfigState {
  id: number;
  mode: AggregationMode;
  capsuleMode: CapsuleModeKey;
  yValueBinMode: YValueBinMode;
  valid: boolean;
  yValueBinMin?: number;
  yValueBinMax?: number;
  binSize?: number;
  numberOfBins?: number;
  item?: Item;
  conditionProperty?: string;
  selectedProperty?: string;
  timeBucket?: AggregationTimeBucket;
}

export const DEFAULT_AGGREGATION_CONFIG: AggregationConfigState = {
  id: 1,
  mode: AGGREGATION_MODES.Y_VALUE,
  capsuleMode: CAPSULE_MODES[0].key,
  yValueBinMode: Y_VALUE_BIN_MODES.SIZE,
  valid: false,
};

export interface AggregationStoreConfig {
  stat?: StoredStatistic;
  statistic?: string | null;
  timeUnits?: string;
  aggregationConfigs?: AggregationConfigState[];
}

/** Narrows an arbitrary item down to the fields persisted on an aggregation config entry. */
export function toAggregationBinStoreItem(item?: ToolItem | Item | null): Item | undefined {
  const id = item?.id;
  if (!id) {
    return undefined;
  }

  const base: Item = {
    id,
    ...(item?.name ? { name: item.name } : {}),
  };

  const optionalFields = _.pick(item, [
    'itemType',
    'redacted',
    'valueUnitOfMeasure',
    'signalMetadata',
    'conditionMetadata',
    'assets',
  ]) as Partial<Item>;

  return {
    ...base,
    ...optionalFields,
  };
}

/**
 * Validates the provided aggregation config. Based on the mode property different attributes must be provided for a
 * config to be valid.
 */
export function validateAggregationConfig(config: AggregationConfigState): boolean {
  if (config.mode === AGGREGATION_MODES.Y_VALUE) {
    if (
      _.isNil(config.yValueBinMin) ||
      _.isNil(config.yValueBinMax) ||
      _.isNil(config.binSize) ||
      _.isEmpty(config.item)
    ) {
      return false;
    }

    return (
      validateBinConfig({
        yValueBinMin: config.yValueBinMin,
        yValueBinMax: config.yValueBinMax,
        binSize: config.binSize,
        numberOfBins: config.numberOfBins,
      }) === '' && !_.isUndefined(config.yValueBinMode)
    );
  }

  if (config.mode === AGGREGATION_MODES.CONDITION) {
    return !_.isEmpty(config.conditionProperty) && !_.isEmpty(config.item);
  }

  return !_.isEmpty(config.timeBucket);
}

/**
 * Computes the next `signalToAggregate` value. Sanitizes the incoming item down to the persisted tool-item
 * properties and returns the current value unchanged when the sanitized item is deeply equal, so the store can avoid
 * a redundant update.
 */
export function nextAggregationBinSignalToAggregate(
  current: ToolItem | undefined,
  item: ToolItem | { id: string } | string | null | undefined,
): ToolItem | undefined {
  const normalized = typeof item === 'string' ? { id: item } : item;
  const sanitized = (sanitizeToolItem(normalized) ?? undefined) as ToolItem | undefined;

  return _.isEqual(current, sanitized) ? current : sanitized;
}

/**
 * Maps the saved UIConfig to the current aggregation config shape. Also updates from the old statistic key to the
 * new ones defined by SAMPLE_FROM_SCALARS.VALUE_METHODS. NOTE: percentGood used to be supported, but it is no longer
 * supported so it will fall through and show an empty statistics selector when edited.
 *
 * Also defaults the capsuleMode for condition/time-based configs to overlaps, and resolves each config entry's item
 * from the formula parameters.
 */
export function migrateAggregationBinConfig<TConfig extends ToolConfigParams>(
  config: TConfig,
  parameters: ToolParameter[],
): TConfig {
  const aggregationConfig = config as TConfig & AggregationStoreConfig;

  if (!_.some(SAMPLE_FROM_SCALARS.VALUE_METHODS, ['key', aggregationConfig.statistic])) {
    const statistic = aggregationConfig.statistic;
    aggregationConfig.statistic =
      statistic === 'StdDev' ? 'standardDeviation' : statistic ? _.toLower(statistic) : (statistic ?? null);
  }

  // Only rewrite the entries when the saved config actually has them; mapping over an absent value would replace it
  // with an empty array and wipe out the default config entry when the store is rehydrated.
  if (aggregationConfig.aggregationConfigs) {
    aggregationConfig.aggregationConfigs = _.map(aggregationConfig.aggregationConfigs, (aggregationConfigEntry) => {
      const entry = { ...aggregationConfigEntry };

      if (entry.mode === AGGREGATION_MODES.CONDITION || entry.mode === AGGREGATION_MODES.TIME) {
        if (_.isUndefined(entry.capsuleMode)) {
          entry.capsuleMode = DEFAULT_AGGREGATION_CONFIG.capsuleMode;
        }
      }

      if (_.has(entry, 'selectedProperty')) {
        entry.conditionProperty = entry.selectedProperty;
        delete entry.selectedProperty;
      }

      const parameterItem = _.chain(parameters)
        .find((parameter) => toNumber(String(parameter.name ?? '').replace(/.*?(\d+)$/, '$1')) === entry.id)
        .get('item')
        .value() as ToolItem | undefined;

      const storeItem = toAggregationBinStoreItem(parameterItem);
      if (storeItem) {
        entry.item = storeItem;
      }

      return entry;
    });
  }

  if (_.isUndefined(aggregationConfig.stat)) {
    aggregationConfig.stat = {
      key: aggregationConfig.statistic ?? null,
      timeUnits: aggregationConfig.timeUnits ?? 's',
    };
    delete aggregationConfig.statistic;
    delete aggregationConfig.timeUnits;
  }

  return config;
}

/**
 * Builds the UI config object that is persisted with the aggregation-bin calculated item from the current store
 * state. Removes each config entry's `item` since the source of truth is the formula parameters.
 */
export function getAggregationBinConfigParams(state: AggregationBinState): ToolConfigParams {
  const configParams = getToolConfigParams({
    serializedState: state as unknown as Record<string, ToolConfigValue>,
    toolType: TREND_TOOLS.AGGREGATION_BINS_TABLE,
    parameterDefinitionKeys: ['signalToAggregate'],
    advancedParametersCollapsed: state.advancedParametersCollapsed,
  });

  const aggregationConfigs = configParams.aggregationConfigs as unknown as AggregationConfigState[] | undefined;

  return {
    ...configParams,
    aggregationConfigs: aggregationConfigs?.map(({ item, ...rest }) => rest),
  } as ToolConfigParams;
}

const validateNumber = (input: number | undefined) =>
  String(input ?? '').trim() !== '' && Number.isFinite(Number(input));

/**
 * Validates the bin configuration for bins by y-value. Returns one of BIN_ERRORS or '' if the param is valid.
 *
 * @param param - param defining a bin configuration
 * @param [param.yValueBinMin] -  minimum y-value to be used for creating bins by y-value. Only
 *   applies to AGGREGATION_MODES.Y_VALUE
 * @param [param.yValueBinMax] -  maximum y-value to be used for creating bins by y-value. Only
 *   applies to AGGREGATION_MODES.Y_VALUE
 * @param [param.binSize] - size of the bin. Only applies to AGGREGATION_MODES.Y_VALUE
 * @param [param.numberOfBins] - number of bins. Only applies to AGGREGATION_MODES.Y_VALUE
 * @returns {String} on of BIN_ERRORS if the param is invalid, or an empty String if the param is valid
 */
export function validateBinConfig(param: {
  yValueBinMin: number;
  yValueBinMax: number;
  binSize: number;
  numberOfBins?: number;
}) {
  const min = Number(param.yValueBinMin);
  const max = Number(param.yValueBinMax);
  const binSize = Number(param.binSize);
  const numberOfBins = Number(param.numberOfBins);

  if ((param.numberOfBins != null && !Number.isFinite(numberOfBins)) || numberOfBins < 1) {
    return BIN_ERRORS.INT_REQUIRED;
  }

  if (!Number.isFinite(min)) {
    return BIN_ERRORS.MIN_NOT_A_NUMBER;
  } else if (!Number.isFinite(max)) {
    return BIN_ERRORS.MAX_NOT_A_NUMBER;
  } else if (min > max) {
    return BIN_ERRORS.MIN_EXCEEDS_MAX;
  } else if (binSize <= 0) {
    return BIN_ERRORS.INVALID_BIN_SIZE;
  }

  if (
    validateNumber(param.yValueBinMin) &&
    validateNumber(param.yValueBinMax) &&
    (validateNumber(param.numberOfBins) || validateNumber(param.binSize))
  ) {
    return '';
  }
}
